import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { subscriptionService } from '../services/subscriptionService';
import { setCredentials } from '../store/authSlice'; // Import setCredentials to update user state

export const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifySession = async () => {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');

      if (!sessionId) {
        setError('Missing payment session information.');
        setStatus('error');
        return;
      }

      try {
        const response = await subscriptionService.verifyPayment(sessionId);
        if (response.success && response.user) {
          // Update user state in Redux store and local storage
          const token = localStorage.getItem('token'); // Re-use existing token
          if (token) {
            dispatch(setCredentials({ user: response.user, token }));
          }
          setStatus('success');
        } else {
          setError(response.message || 'Payment verification failed.');
          setStatus('error');
        }
      } catch (err: any) {
        console.error('Payment verification error:', err);
        setError(err.response?.data?.message || 'An error occurred during payment verification.');
        setStatus('error');
      }
    };

    verifySession();
  }, [location, dispatch]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-xl text-center"
      >
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Verifying Payment...</h2>
            <p className="text-gray-600 dark:text-gray-300">Please wait while we confirm your subscription.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Successful!</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">Welcome to Premium! Your subscription is now active.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg text-sm font-semibold hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Go to Dashboard
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Failed</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{error || 'An unexpected error occurred.'}</p>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full px-6 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              Return to Pricing
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};
